"use client";

import { c as _c } from "react/compiler-runtime";
import { IconShell } from "@/components/ui/glass-icon";
import { CardIconTile } from "@/components/workflow/CardIconTile";
import { cn } from "@/lib/utils";
import type { CardIcon, CardIconChainInfo, CardIconConnectorType, CardIconDef } from "@/lib/workflow/cardIcons";
export const MAX_CARD_TILES = 4;
export const CARD_TILE_WRAP_CLASS = "border-ds-alpha-shadow-10 bg-ds-alpha-white-40 rounded-ds-14 relative flex items-center border p-[5px] backdrop-blur-md";
export const CARD_TILE_INNER_CLASS = cn("bg-ds-surface-secondary rounded-ds-sm size-12", "shadow-ds-b3-card");
export const CARD_TILE_ICON_CLASS = "h-full w-full object-contain";
export function cardIconKey(icon: CardIcon): string {
  if (icon.kind === "connector") return `c:${icon.brandName}`;
  if (icon.kind === "chain") return `ch:${icon.chainId}`;
  return `n:${icon.type}:${icon.isTrigger ? "t" : "a"}`;
}
interface CardIconStripProps {
  icons: CardIcon[];
  triggers: CardIconDef[];
  actions: CardIconDef[];
  connectorTypes: CardIconConnectorType[];
  chainInfoMap?: Map<number, CardIconChainInfo>;
  /** Maximum visible tiles. The last slot is reserved for a "+N" overflow tile when needed. */
  maxTiles?: number;
  /** Rendered when `icons` is empty. */
  emptyFallback?: React.ReactNode;
  className?: string;
}

/**
 * Presentational tile strip used by every workflow / template card. Caller is
 * responsible for prioritization (`prioritizeCardIcons`) and any filtering
 * (e.g. `hasBrandVisual`); this component just renders the tiles + overflow.
 */
export function CardIconStrip(t0) {
  const $ = _c(21);
  const {
    icons,
    triggers,
    actions,
    connectorTypes,
    chainInfoMap,
    maxTiles: t1,
    emptyFallback,
    className
  } = t0;
  const maxTiles = t1 === undefined ? MAX_CARD_TILES : t1;
  if (icons.length === 0) {
    return emptyFallback ?? null;
  }
  const overflow = icons.length > maxTiles;
  const visibleCount = overflow ? maxTiles - 1 : icons.length;
  const hiddenCount = icons.length - visibleCount;
  let t2;
  if ($[0] !== className) {
    t2 = cn("flex w-full max-w-[286px] flex-wrap items-center justify-center gap-3", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== actions || $[3] !== chainInfoMap || $[4] !== connectorTypes || $[5] !== icons || $[6] !== triggers || $[7] !== visibleCount) {
    let t4;
    if ($[9] !== actions || $[10] !== chainInfoMap || $[11] !== connectorTypes || $[12] !== triggers) {
      t4 = icon => <CardIconTile key={cardIconKey(icon)} icon={icon} triggers={triggers} actions={actions} connectorTypes={connectorTypes} chainInfoMap={chainInfoMap} nodeTemplate="b3os-app-flat" wrapClassName={CARD_TILE_WRAP_CLASS} innerClassName={CARD_TILE_INNER_CLASS} iconClassName={CARD_TILE_ICON_CLASS} />;
      $[9] = actions;
      $[10] = chainInfoMap;
      $[11] = connectorTypes;
      $[12] = triggers;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = icons.slice(0, visibleCount).map(t4);
    $[2] = actions;
    $[3] = chainInfoMap;
    $[4] = connectorTypes;
    $[5] = icons;
    $[6] = triggers;
    $[7] = visibleCount;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[14] !== hiddenCount || $[15] !== overflow) {
    t4 = overflow && <div className={CARD_TILE_WRAP_CLASS}><IconShell className={cn(CARD_TILE_INNER_CLASS, "from-ds-neutral-700 to-ds-neutral-900 dark:from-ds-neutral-300 dark:to-ds-neutral-500 bg-gradient-to-b")}><span className="dark:text-ds-neutral-900 text-sm font-semibold text-white">+{hiddenCount}</span></IconShell></div>;
    $[14] = hiddenCount;
    $[15] = overflow;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t2 || $[18] !== t3 || $[19] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}